import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, AlertCircle, Clock, Send, 
  Loader2, Building2, MapPin, Calendar, MessageSquare,
  AlertTriangle, FileQuestion
} from 'lucide-react';

export default function PublicSIClarification() {
  const { tokenId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [siData, setSiData] = useState(null);
  
  const [formData, setFormData] = useState({
    response_text: '',
    supporting_documents: [],
    revised_drawing_refs: [],
    further_clarification_needed: false,
    further_questions: ''
  });

  const [newDrawingRef, setNewDrawingRef] = useState('');

  useEffect(() => {
    loadSIDetails();
  }, [tokenId]);

  const loadSIDetails = async () => {
    try {
      const response = await api.publicSIClarification.getSIDetails(tokenId);
      setSiData(response.data);
    } catch (err) {
      console.error('Error loading SI:', err);
      if (err.response?.status === 410) {
        setError('This link has expired. Please request a new clarification link from the contractor.');
      } else if (err.response?.status === 404) {
        setError('Invalid link. Please check the URL or request a new link.');
      } else {
        setError('Unable to load the Site Instruction. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const addDrawingRef = () => {
    if (newDrawingRef.trim()) {
      setFormData({
        ...formData,
        revised_drawing_refs: [...formData.revised_drawing_refs, newDrawingRef.trim()]
      });
      setNewDrawingRef('');
    }
  };

  const removeDrawingRef = (index) => {
    setFormData({
      ...formData,
      revised_drawing_refs: formData.revised_drawing_refs.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    if (!formData.response_text.trim()) {
      toast.error('Please enter your clarification response');
      return;
    }

    setSubmitting(true);
    try {
      await api.publicSIClarification.submitClarification(tokenId, formData);
      setSubmitted(true);
      toast.success('Clarification submitted successfully!');
    } catch (err) {
      console.error('Error submitting clarification:', err);
      if (err.response?.status === 410) {
        setError('This link has expired. Please request a new clarification link from the contractor.');
      } else {
        toast.error('Failed to submit clarification. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate if overdue
  const isOverdue = siData?.si?.clarification_due_date && 
    new Date(siData.si.clarification_due_date) < new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Site Instruction...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Unable to Access</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Clarification Submitted!</h1>
          <p className="text-slate-600 mb-4">
            Thank you for providing clarification on SI {siData?.si?.si_number}. 
            The contractor has been notified of your response.
          </p>
          {formData.further_clarification_needed && (
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
              You indicated that further clarification is needed. 
              The contractor will review and respond.
            </p>
          )}
          <p className="text-sm text-slate-400 mt-4">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-cyan-900 text-white rounded-t-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <FileQuestion className="h-6 w-6" />
            <span className="text-sm uppercase tracking-wider opacity-80">Clarification Request</span>
          </div>
          <h1 className="text-2xl font-bold font-barlow">
            Site Instruction {siData?.si?.si_number}
          </h1>
          <p className="text-cyan-200 mt-1">{siData?.project_name}</p>
          <div className="flex items-center gap-4 mt-3 text-sm">
            <span className="text-cyan-200">
              From: {siData?.company_name}
            </span>
            {isOverdue && (
              <Badge className="bg-red-500 text-white">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Overdue
              </Badge>
            )}
          </div>
        </div>

        {/* Due Date Warning */}
        {siData?.si?.clarification_due_date && (
          <div className={`p-4 flex items-center gap-3 ${
            isOverdue 
              ? 'bg-red-50 border-l-4 border-red-500' 
              : 'bg-amber-50 border-l-4 border-amber-500'
          }`}>
            <Clock className={`h-5 w-5 ${isOverdue ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className={`font-medium ${isOverdue ? 'text-red-800' : 'text-amber-800'}`}>
                {isOverdue ? 'Response Overdue' : 'Response Due'}
              </p>
              <p className={`text-sm ${isOverdue ? 'text-red-600' : 'text-amber-600'}`}>
                {new Date(siData.si.clarification_due_date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        )}

        {/* SI Details Card */}
        <div className="bg-white border border-slate-200 shadow-sm p-6">
          <h2 className="font-semibold text-lg text-slate-900 mb-4">{siData?.si?.si_title}</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Building2 className="h-4 w-4" />
              <span>Discipline: {siData?.si?.discipline}</span>
            </div>
            {siData?.si?.issue_date && (
              <div className="flex items-center gap-2 text-slate-600">
                <Calendar className="h-4 w-4" />
                <span>Issue Date: {siData?.si?.issue_date}</span>
              </div>
            )}
            {siData?.si?.location_area && (
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="h-4 w-4" />
                <span>Location: {siData?.si?.location_area}</span>
              </div>
            )}
          </div>

          {siData?.si?.si_description && (
            <div className="bg-slate-50 rounded p-4 text-sm text-slate-700 whitespace-pre-wrap mb-4">
              {siData?.si?.si_description}
            </div>
          )}

          {siData?.si?.drawings_referenced?.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Referenced Drawings</p>
              <div className="flex flex-wrap gap-2">
                {siData.si.drawings_referenced.map((d, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {d.drawing_number} Rev {d.revision}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Clarification Questions */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mt-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-5 w-5 text-cyan-700" />
              <h3 className="font-semibold text-cyan-900">Questions Requiring Clarification</h3>
            </div>
            <div className="text-slate-700 whitespace-pre-wrap">
              {siData?.si?.clarification_questions || 'No specific questions provided.'}
            </div>
          </div>
        </div>

        {/* Previous Clarifications */}
        {siData?.previous_clarifications?.length > 0 && (
          <div className="bg-white border border-t-0 border-slate-200 shadow-sm p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Previous Clarifications</h3>
            <div className="space-y-3">
              {siData.previous_clarifications.map((c, i) => (
                <div key={i} className="bg-slate-50 rounded p-3 text-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium">{c.consultant_name}</span>
                    <span className="text-xs text-slate-400">
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-slate-600">{c.response_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Response Form */}
        <div className="bg-white border border-t-0 border-slate-200 shadow-sm rounded-b-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-4 w-4 text-cyan-600" />
            <p className="text-sm text-slate-600">
              Responding as <span className="font-semibold">{siData?.consultant_name}</span>
              {siData?.company_name && ` (${siData?.company_name})`}
            </p>
          </div>

          <div className="space-y-6">
            {/* Response Text */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Your Clarification Response <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={formData.response_text}
                onChange={(e) => setFormData({ ...formData, response_text: e.target.value })}
                placeholder="Please provide your clarification response addressing the questions above..."
                rows={6}
                className="rounded-sm"
                data-testid="clarification-text-input"
              />
            </div>

            {/* Revised Drawing References */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Revised Drawing References (Optional)
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newDrawingRef}
                  onChange={(e) => setNewDrawingRef(e.target.value)}
                  placeholder="e.g., DWG-001 Rev B"
                  className="rounded-sm"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDrawingRef())}
                />
                <Button type="button" variant="outline" onClick={addDrawingRef} className="rounded-sm">
                  Add
                </Button>
              </div>
              {formData.revised_drawing_refs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.revised_drawing_refs.map((ref, i) => (
                    <Badge 
                      key={i} 
                      className="bg-slate-100 text-slate-700 cursor-pointer hover:bg-red-100"
                      onClick={() => removeDrawingRef(i)}
                    >
                      {ref} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Further Clarification Needed */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
              <Checkbox
                id="further-clarification"
                checked={formData.further_clarification_needed}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  further_clarification_needed: checked 
                })}
              />
              <div>
                <Label 
                  htmlFor="further-clarification" 
                  className="text-sm font-semibold text-amber-900 cursor-pointer"
                >
                  I require further information from the Contractor
                </Label>
                <p className="text-xs text-amber-700 mt-1">
                  Check this if you need additional information before providing a complete clarification.
                </p>
              </div>
            </div>

            {formData.further_clarification_needed && (
              <div>
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                  What information do you need?
                </Label>
                <Textarea
                  value={formData.further_questions}
                  onChange={(e) => setFormData({ ...formData, further_questions: e.target.value })}
                  placeholder="Please specify what additional information you require from the contractor..."
                  rows={3}
                  className="rounded-sm"
                />
              </div>
            )}

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.response_text.trim()}
              className="w-full bg-cyan-700 text-white hover:bg-cyan-800 rounded-sm py-6 text-base"
              data-testid="submit-clarification-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Submit Clarification
                </>
              )}
            </Button>

            <p className="text-xs text-slate-400 text-center">
              Link expires: {siData?.expires_at ? new Date(siData.expires_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
